import { Component, ViewChild, AfterViewInit, ViewChildren, Input, ElementRef, QueryList } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IAbstractView } from '@core/interfaces';
import { StoresPresenter } from './stores.presenter';
import { IStoresComponent, IStoresPresenter, IStoreStatus } from './interfaces';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LocationMapComponent } from '@shared/components/location-map/location-map.component';
import { Address, Schedule, Store as StoreModel } from '@core/models';
import { SwitchComponent } from '@shared/components/switch-v2/switch.component';
import { IconComponent } from '@shared/components';
import { ScrollContainerComponent } from '@shared/components/scroll-container/scroll-container.component';
import { OrderTypeEnum } from '@core/enums';
import { ServiceType } from '@core/services/switch.service';
import { AdressesLocatorComponent, StoresListComponent } from './components';
import { AdressesLocatorModeEnum } from './components/adresses-locator/enums';
import {IStoreSelectedEvent } from './components/stores-list/interfaces';

@Component({
	selector: 'app-store-view',
	templateUrl: './stores.component.html',
	styleUrls: ['./stores.component.scss'],
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		RouterModule,
		LocationMapComponent,
		SwitchComponent,
		ScrollContainerComponent,
		IconComponent,
		AdressesLocatorComponent,
		StoresListComponent,
	],
	providers: [StoresPresenter],
	standalone: true,
})
export class StoresComponent extends IAbstractView<IStoresPresenter> implements IStoresComponent, AfterViewInit {
	readonly AdressesLocatorModeEnum = AdressesLocatorModeEnum;
	auth$ = this.presenter.auth$;
	formGroup = this.presenter.formGroup;
	isLoading$ = this.presenter.isLoading$;
	isLoggedIn = this.presenter.isLoggedIn;
	orderType$ = this.presenter.orderType$;
	address$ = this.presenter.address$;
	autocompleteResults$ = this.presenter.autocompleteResults$;
	storesWithDistanceSignal$ = this.presenter.storesWithDistanceSignal$;
	storesWithDistance$ = this.presenter.storesWithDistance$;
	showNameAddressInput = this.presenter.showNameAddressInput;
	get searchAddressInput(): string {
		return this.presenter.searchAddressInput ?? '';
	}
	set searchAddressInput(value: string | null) {
		this.presenter.searchAddressInput = value ?? '';
	}
	idAddressDefault = this.presenter.idAddressDefault;
	storeInDelivery$ = this.presenter.storeInDelivery$;
	clientLocation$ = this.presenter.clientLocation$;
	translations = this.presenter.translations;
	errorMessages = this.presenter.errorMessages;
	selectedOption: ServiceType = OrderTypeEnum.Pickup

	@Input() set showMap(value: boolean) {
		this.presenter.showMap = value;
	}

	get showMap(): boolean {
		return this.IsModal ? true : this.presenter.showMap;
	}

	@Input() set IsModal(value: boolean) {
		this.presenter.IsModal = value;
	}

	get IsModal(): boolean {
		return this.presenter.IsModal;
	}

	// Alias en camelCase por si Ionic inyecta la prop así
	@Input() set isModal(value: boolean) {
		this.presenter.IsModal = value;
	}

	@ViewChild('adressesLocator')
	adressesLocator?: AdressesLocatorComponent;

	@ViewChild('locationInput', { read: ElementRef, static: false })
	locationInput!: ElementRef<HTMLInputElement>;

	@ViewChildren('locationInput', { read: ElementRef })
	locationInputs!: QueryList<ElementRef<HTMLInputElement>>;

	@ViewChild('switchHome') switchHome!: SwitchComponent;

	constructor(presenter: StoresPresenter) {
		super(presenter);
	}

	updateSwitchUI(): void {
		if (this.switchHome && this.switchHome.presenter) {
			const isDelivery = this.presenter.selectedOption === OrderTypeEnum.Delivery;
			this.switchHome.presenter.checked = isDelivery;
			this.switchHome.checked = isDelivery;
		}
	}

	getRangeTimeByDay(store: StoreModel, code: 'DELIVERY' | 'PICK-UP'): { apertura: string; cierre: string } | null {
		return this.presenter.getRangeTimeByDay(store, code);
	}

	onSwitchChanged(): void {
		this.presenter.onSwitchChanged();
	}

	addressAvailable(): boolean {
		return this.presenter.addressAvailable();
	}

	onOpenCartMenu(): void {
		this.presenter.onOpenCartMenu();
	}

	updateDeliveryAddress(event: Event): void {
		this.presenter.updateDeliveryAddress(event);
	}

	closeAddressForm(): void {
		this.presenter.closeAddressForm();
	}

	findUserLocation(): void {
		this.presenter.findUserLocation();
	}

	confirmDeliveryAddress(): void {
		this.presenter.confirmDeliveryAddress();
	}

	// En StoresComponent (para poder usarla en el template)
	getStoreStatus(horarios: Schedule[]): IStoreStatus {
		return this.presenter.getStoreStatus(horarios);
	}

	selectAutocomplete(result: google.maps.places.AutocompletePrediction): void {
		this.presenter.selectAutocomplete(result, this.locationInput);
	}

	saveManualAddress(input?: HTMLInputElement | string | null): void {
		this.presenter.saveManualAddress(input);
	}

	onApartmentOrSuiteChange(event: Event | string): void {
		this.presenter.onApartmentOrSuiteChange(event);
	}

	onDeliveryInstructionsChange(event: Event | string): void {
		this.presenter.onDeliveryInstructionsChange(event);
	}

	onFavoriteChange(event: Event | boolean): void {
		this.presenter.onFavoriteChange(event);
	}

	onAddressClick(address: Address): void {
		this.presenter.onAddressClick(address);
	}

	closeModal(): void {
		this.presenter.closeModal();
	}

	onAddressInputChange(event: Event | string): void {
		this.presenter.onAddressInputChange(event);
	}

	onAddressSelected(address: string): void {
		this.presenter.saveManualAddress(address);
	}

	onSavedAddressSelected(address: string): void {
		this.presenter.saveManualAddress(address);
		this.adressesLocator?.saveManualAddress(address);
	}

	onMapAddressSelected(address: string): void {
		this.presenter.onMapAddressSelected(address);
		this.adressesLocator?.saveManualAddress(address);
	}

	onStoreSelected(event: IStoreSelectedEvent): void {
		this.presenter.onStoreSelected(event);
	}
}
